import {
  DescMessage,
  JsonReadOptions,
  JsonValue,
  MessageInitShape,
  MessageShape,
  isMessage,

  // eslint-disable-next-line no-restricted-imports
  clone as originalClone,
  // eslint-disable-next-line no-restricted-imports
  create as originalCreate,
  // eslint-disable-next-line no-restricted-imports
  fromJson as originalFromJson,
} from '@bufbuild/protobuf';

export { type JsonObject, type JsonValue, isMessage, equals } from '@bufbuild/protobuf';
export { type Timestamp, TimestampSchema } from '@bufbuild/protobuf/wkt';
export * from '@/connect/gen/common/protos/types_pb';

export * from './utils';
export * from './consts';
export * from './result';
export * from './transport';

export * from './services/agreements';
export * from './services/companies';
export * from './services/loans';
export * from './services/login';
export * from './services/security';

// TODO: Remove in Vue 3
function makeReactifiable<Desc extends DescMessage>(schema: Desc, message: MessageShape<Desc>) {
  for (const field of schema.fields) {
    const fieldName = field.localName;
    if (!(fieldName in message)) {
      // all the fields should exist, at least in `undefined` state,
      // otherwise Vue 2.7 reactivity system won't track them
      message[fieldName] = undefined;
    }
  }
}

/**
 * Create a new message instance.
 *
 * The second argument is an optional initializer object, where all fields are
 * optional. It shouldn't be a `Message` of the same schema, If it is, use `clone` instead.
 */
export function create<Desc extends DescMessage>(
  schema: Desc,
  init?: MessageInitShape<Desc>
): MessageShape<Desc> {
  if (init && isMessage(init, schema)) {
    throw new Error('Use `clone` on the existing message of the same schema instead');
  }

  const result = originalCreate(schema, init);
  makeReactifiable(schema, originalCreate(schema, init));
  return result;
}

/**
 * Parse a message from a JSON value.
 *
 * It uses `create` under the hood
 */
export function fromJson<Desc extends DescMessage>(
  schema: Desc,
  json: JsonValue,
  options?: Partial<JsonReadOptions>
): MessageShape<Desc> {
  const result = originalFromJson<Desc>(schema, json, options);
  makeReactifiable(schema, result);
  return result;
}

/**
 * Create a deep copy of a message, including extensions and unknown fields.
 */
export function clone<Desc extends DescMessage>(
  schema: Desc,
  message: MessageShape<Desc>
): MessageShape<Desc> {
  const result = originalClone<Desc>(schema, message);
  makeReactifiable(schema, result);
  return result;
}
